import { WBFlex } from '@admiin-com/ds-web';
import { AnnotationsUnion } from '@nutrient-sdk/viewer';
import { forwardRef, useEffect, useRef, memo } from 'react';
import { PdfPlaceholderV2 } from '../PdfPlaceholder/PdfPlaceholder';
import { createCustomSignatureNode } from '../../helpers/signature';
import { stripPdfIdFromAnnotations } from '../../helpers/tasks';
import { getNutrientConfig } from '../../features/hooks/useNutrientPreload/useNutrientPreload';

// if domain is local host
export interface PdfViewerProps {
  documentUrl?: string;
  height?: any;
  headless?: boolean;
  annotations?: any;
  onPdfLoad?: () => void;
}
const renderConfigurations: Record<any, any> = {};
export const PdfViewer = memo(
  forwardRef(
    (
      { height, headless, documentUrl, annotations, onPdfLoad }: PdfViewerProps,
      instanceRef: any
    ) => {
      const containerRef = useRef(null);

      useEffect(() => {
        const container = containerRef.current; // This `useRef` instance will render the PDF.

        let NutrientViewer: any;

        if (!instanceRef) {
          instanceRef = { current: null };
        }

        const getAnnotationRenderers = ({
          annotation,
        }: {
          annotation: AnnotationsUnion;
        }) => {
          // Use cached render configuration
          if (renderConfigurations[annotation.id]) {
            return renderConfigurations[annotation.id];
          }

          const { customData } = annotation;
          const placeholderUi = <PdfPlaceholderV2 customData={customData} />;
          const customNode = createCustomSignatureNode(
            placeholderUi,
            customData
          );

          renderConfigurations[annotation.id] = {
            node: customNode,
            append: true,
          };
          return renderConfigurations[annotation.id] || null;
        };

        (async function () {
          try {
            NutrientViewer = await import('@nutrient-sdk/viewer');
            console.log(
              'NutrientViewer.current viewer: ',
              NutrientViewer.current
            );

            NutrientViewer.unload(container); // Ensure that there's only one Nutrient Viewer instance.
            const nutrientParams: any = {
              // TODO: types for init nutrient viewer
              container,
              document: documentUrl,
              ...getNutrientConfig(),
              toolbarItems: [],

              disableTextSelection: false,
              customRenderers: {
                Annotation: getAnnotationRenderers,
              },
              styleSheets: ['/viewer.css'],
              initialViewState: new NutrientViewer.ViewState({
                readOnly: true,
              }),
            };

            if (annotations) {
              nutrientParams.instantJSON = JSON.parse(
                stripPdfIdFromAnnotations(annotations) ?? ''
              );
            }
            instanceRef.current = await NutrientViewer.load(nutrientParams);

            const viewState = instanceRef?.current?.viewState;
            instanceRef?.current?.setViewState(
              viewState.set('showToolbar', false)
            );
            onPdfLoad && onPdfLoad();
          } catch (error) {
            console.log(error);
          }
        })();

        return () => NutrientViewer && NutrientViewer.unload(container);
      }, [documentUrl, instanceRef]);

      // This div element will render the document to the DOM.
      return (
        <WBFlex
          ref={containerRef}
          className="pdf-viewer"
          flex={1}
          {...(height ? { height } : {})}
          // sx={{ width: '100%', height: height ?? ['250px', '150px'] }}
        />
      );
    }
  )
);

export default PdfViewer;
